import { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { useAlert } from 'react-alert';
import { Prod01 } from '../../constants/ActionType';
import { isPracticeOrg } from '../../utils/patientForm';
import { isSuperAdmin } from './StaffOrgAssign';

const errMsg = (error) => error?.response?.data?.message || error.message;

/**
 * Single organization select for a patient or device (organizationId).
 * - Create forms (no resourceId): controlled via value/onChange. Super Admin picks any active org;
 *   others get their own org (editable only when they belong to several).
 * - Profile/edit (resourceId set): Super Admin changes it through the audited
 *   PUT /organizations/:kind/:id; everyone else sees it read-only.
 * Inactive organizations are never offered.
 */
const ResourceOrgAssign = ({ kind, resourceId, organizationId, value, onChange, onSaved, label = 'Organization' }) => {
  const { user } = useSelector((state) => state.auth);
  const alert = useAlert();
  const superAdmin = isSuperAdmin(user);
  const editMode = Boolean(resourceId);
  const [orgs, setOrgs] = useState([]);
  const [current, setCurrent] = useState(organizationId ? String(organizationId) : null);
  const [selected, setSelected] = useState(organizationId ? String(organizationId) : '');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    const reqs = [axios.get(`${Prod01}/${editMode && superAdmin ? 'organizations' : 'general/organizations'}`)];
    if (editMode && superAdmin) reqs.push(axios.get(`${Prod01}/organizations/${kind}/${resourceId}`));
    Promise.all(reqs)
      .then(([o, r]) => {
        if (!alive) return;
        const list = o.data || [];
        setOrgs(list);
        if (r) {
          setCurrent(r.data.organizationId || null);
          setSelected(r.data.organizationId || '');
        }
        const active = list.filter((x) => x.status === 'active');
        if (!editMode && !superAdmin && active.length === 1 && !value && onChange) onChange(String(active[0]._id));
        setLoaded(true);
      })
      .catch((e) => alive && alert.error(errMsg(e)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, resourceId, superAdmin]);

  const activeOrgs = orgs.filter((o) => o.status === 'active' && (kind !== 'patient' || isPracticeOrg(o)));
  const options = activeOrgs.map((o) => ({ value: String(o._id), label: o.name }));
  const labelFor = (id) => {
    if (!id) return null;
    const hit = options.find((x) => x.value === id);
    if (hit) return hit;
    const org = orgs.find((o) => String(o._id) === id);
    return { value: id, label: org ? `${org.name} (inactive)` : 'Assigned organization' };
  };

  if (!editMode) {
    const canPick = superAdmin || options.length > 1;
    return (
      <div>
        <label className="form-label mt-3">
          {label} <span style={{ color: 'red', fontWeight: 'bold' }}> *</span>
        </label>
        <Select
          options={options}
          value={labelFor(value ? String(value) : '')}
          isDisabled={!canPick}
          placeholder={canPick ? 'Select organization' : 'Your organization'}
          onChange={(opt) => onChange && onChange(opt ? opt.value : '')}
        />
        {!canPick ? <small className="text-muted">Set automatically to your organization.</small> : null}
      </div>
    );
  }

  const dirty = superAdmin && current !== selected && Boolean(selected);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${Prod01}/organizations/${kind}/${resourceId}`, { organizationId: selected });
      setCurrent(data.organizationId);
      setSelected(data.organizationId);
      alert.success('Organization updated');
      if (onSaved) onSaved(data.organizationId);
    } catch (e) {
      alert.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 p-3" style={{ border: '1px solid #dee2e6', borderRadius: 6 }} data-testid={`org-assign-${kind}`}>
      <label className="form-label">
        <b>{label}</b> <small className="text-muted">({superAdmin ? 'Super Admin only' : 'read-only'})</small>
      </label>
      <Select
        options={options}
        value={labelFor(selected)}
        placeholder={superAdmin ? 'Select organization' : 'No organization'}
        onChange={(opt) => setSelected(opt ? opt.value : '')}
        isDisabled={!superAdmin || !loaded || saving}
      />
      {superAdmin ? (
        <div className="mt-2">
          <button type="button" className="edit-pill" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving...' : 'Save Organization'}
          </button>
        </div>
      ) : (
        <small className="text-muted">Only a Super Admin can change the organization.</small>
      )}
    </div>
  );
};

export default ResourceOrgAssign;
